import { z } from 'zod';
import {
    RpaBuilderWebsiteConfigSchema,
    StoredRpaBuilderWebsiteConfigParser,
    StoredRpaBuilderWebsiteConfigSchema,
    serializeRpaBuilderWebsiteConfigV2,
} from '../backend_config.types';
import {
    type RbsWebsite,
    type RbsWebsiteStorageV1,
    RbsWebsiteStorageVersions,
    type RbsWorkflow,
    type RbsWorkflowMap,
    type RbsWorkflowMapStorageV1,
    type RbsWorkflowStorageV1,
    type RpaBuilderLoadWebsiteConfigurationRequest,
    type RpaBuilderLoadWebsiteConfigurationResponse,
    type RpaBuilderResetWebsiteConfigurationRequest,
    type RpaBuilderResetWebsiteConfigurationResponse,
    type RpaBuilderSaveWebsiteConfigurationRequest,
    type RpaBuilderSaveWebsiteConfigurationResponse,
    type RbsWebsitePatch,
    type RpaBuilderWebsiteConfigurationProjection,
} from '../rbs-website.types';
import type {
    RpaBuilderLoadWebsiteCredentialDefinitionRequest,
    RpaBuilderLoadWebsiteCredentialDefinitionResponse,
    RpaBuilderSaveWebsiteCredentialConfigurationRequest,
    RpaBuilderSaveWebsiteCredentialConfigurationResponse,
} from '../secret.types';
import type { RbsWebsiteStorageVersion } from '../rbs-website.types';
import { IpxRbsWorkflowClass } from '../rbs-ips-workflow.types';
import { IpxRbsWorkflowWebsiteScopeSchema } from './rbs-website-scope.schemas';
import {
    buildStoredJsonValueParser,
    defineStoredJsonContract,
} from '../stored-json-contract.types';
import { normalizeStoredJsonSafeIntegerObjectFields } from '../stored-json-number';
import { JsonValueSchema, NonEmptyStringSchema } from './common.schemas';
import {
    IpxCredentialFieldConfigMapSchema,
    RpaBuilderCredentialDefinitionProjectionSchema,
    SupportedCredentialSystemIdSchema,
} from './secret.schemas';
import { UrlPatternStringArraySchema } from '../backend_config.types';
import {
    IpxRbsWorkflowElementBindingListV1Schema,
    StoredIpxRbsWorkflowElementBindingListParser,
    StoredIpxRbsWorkflowElementBindingListSchema,
    serializeIpxRbsWorkflowElementBindingListV2,
} from './rbs-ips-workflow.schemas';
import {
    IpxDomRpaWorkflowStepListSchema,
    IpxWebResourceModelDocumentSchema,
    StoredIpxDomRpaWorkflowParser,
    StoredIpxDomRpaWorkflowSchema,
    StoredIpxWebResourceModelDocumentParser,
    StoredIpxWebResourceModelDocumentSchema,
    serializeIpxDomRpaWorkflowV2,
    serializeIpxWebResourceModelDocumentV2,
} from './samba-rpa-workflow.schemas';

export const RbsWorkflowSchema: z.ZodType<RbsWorkflow> = z
    .object({
        workflow: IpxDomRpaWorkflowStepListSchema,
        input_bindings: IpxRbsWorkflowElementBindingListV1Schema,
        output_bindings: IpxRbsWorkflowElementBindingListV1Schema,
        class_metadata: z.record(z.string(), JsonValueSchema),
    })
    .strict();

const RbsWorkflowMapObjectSchema = z
    .object({
        [IpxRbsWorkflowClass.ENABLE_PAYMENTS]: z.array(RbsWorkflowSchema),
        [IpxRbsWorkflowClass.TRANSACTION_DETAILS_DETECTION]: z.array(RbsWorkflowSchema),
        [IpxRbsWorkflowClass.ENABLE_RECONCILIATION]: z.array(RbsWorkflowSchema),
        [IpxRbsWorkflowClass.RECONCILE]: z.array(RbsWorkflowSchema),
    })
    .strict();

export const RbsWorkflowMapSchema: z.ZodType<RbsWorkflowMap> = RbsWorkflowMapObjectSchema;

export const RbsWebsiteSchema: z.ZodType<RbsWebsite> = z
    .object({
        rbs_config: RpaBuilderWebsiteConfigSchema,
        web_resource_model: IpxWebResourceModelDocumentSchema.nullable(),
        login_workflow: IpxDomRpaWorkflowStepListSchema.nullable(),
        workflows: RbsWorkflowMapSchema,
    })
    .strict();

export const RbsWebsitePatchSchema: z.ZodType<RbsWebsitePatch> = z
    .object({
        rbs_config: RpaBuilderWebsiteConfigSchema.optional(),
        web_resource_model: IpxWebResourceModelDocumentSchema.nullable().optional(),
        login_workflow: IpxDomRpaWorkflowStepListSchema.nullable().optional(),
        workflows: RbsWorkflowMapObjectSchema.partial().optional(),
    })
    .strict()
    .refine((patch) => Object.keys(patch).length > 0, {
        message: 'At least one RBS website field is required',
    });

export const RpaBuilderWebsiteConfigurationProjectionSchema: z.ZodType<RpaBuilderWebsiteConfigurationProjection> =
    z
        .object({
            website_scope: IpxRbsWorkflowWebsiteScopeSchema,
            rbs_website: RbsWebsiteSchema.nullable(),
            web_resource_model_digest: z
                .string()
                .regex(/^[a-f0-9]{64}$/)
                .nullable(),
            ipx_credential_definition_uuid: z.string().uuid().nullable(),
        })
        .strict()
        .superRefine((configuration, context) => {
            const hasWebResourceModel =
                configuration.rbs_website?.web_resource_model !== null &&
                configuration.rbs_website?.web_resource_model !== undefined;
            if (hasWebResourceModel !== (configuration.web_resource_model_digest !== null)) {
                context.addIssue({
                    code: 'custom',
                    path: ['web_resource_model_digest'],
                    message: 'Website Map and digest must be present together',
                });
            }
        });

export const RpaBuilderLoadWebsiteConfigurationRequestSchema: z.ZodType<RpaBuilderLoadWebsiteConfigurationRequest> =
    z
        .object({
            website_scope: IpxRbsWorkflowWebsiteScopeSchema,
        })
        .strict();

export const RpaBuilderLoadWebsiteConfigurationResponseSchema: z.ZodType<RpaBuilderLoadWebsiteConfigurationResponse> =
    z
        .object({
            configuration: RpaBuilderWebsiteConfigurationProjectionSchema,
        })
        .strict();

export const RpaBuilderSaveWebsiteConfigurationRequestSchema: z.ZodType<RpaBuilderSaveWebsiteConfigurationRequest> =
    z
        .object({
            website_scope: IpxRbsWorkflowWebsiteScopeSchema,
            patch: RbsWebsitePatchSchema,
            ipx_credential_definition_uuid: z.string().uuid().nullable().optional(),
        })
        .strict();

export const RpaBuilderSaveWebsiteConfigurationResponseSchema: z.ZodType<RpaBuilderSaveWebsiteConfigurationResponse> =
    z
        .object({
            configuration: RpaBuilderWebsiteConfigurationProjectionSchema,
        })
        .strict();

export const RpaBuilderResetWebsiteConfigurationRequestSchema: z.ZodType<RpaBuilderResetWebsiteConfigurationRequest> =
    z
        .object({
            website_scope: IpxRbsWorkflowWebsiteScopeSchema,
        })
        .strict();

export const RpaBuilderResetWebsiteConfigurationResponseSchema: z.ZodType<RpaBuilderResetWebsiteConfigurationResponse> =
    z
        .object({
            configuration: RpaBuilderWebsiteConfigurationProjectionSchema,
        })
        .strict();

const RpaBuilderWebsiteCredentialSchemaFieldsSchema = z.object({
    system_id: SupportedCredentialSystemIdSchema,
    credential_definition_key: z.string(),
    trigger_url_patterns: UrlPatternStringArraySchema,
    credential_field_config: IpxCredentialFieldConfigMapSchema,
});

export const RpaBuilderSaveWebsiteCredentialConfigurationRequestSchema: z.ZodType<RpaBuilderSaveWebsiteCredentialConfigurationRequest> =
    z
        .object({
            website_scope: IpxRbsWorkflowWebsiteScopeSchema,
            rbs_website: RbsWebsiteSchema,
            credential_definition: z.union([
                RpaBuilderWebsiteCredentialSchemaFieldsSchema.extend({
                    operation: z.literal('create'),
                }),
                RpaBuilderWebsiteCredentialSchemaFieldsSchema.extend({
                    operation: z.literal('update'),
                    ipx_credential_definition_uuid: z.string().uuid(),
                    expected_revision_hash: NonEmptyStringSchema,
                }),
            ]),
        })
        .strict();

export const RpaBuilderSaveWebsiteCredentialConfigurationResponseSchema: z.ZodType<RpaBuilderSaveWebsiteCredentialConfigurationResponse> =
    z
        .object({
            configuration: RpaBuilderWebsiteConfigurationProjectionSchema,
            credential_definition: RpaBuilderCredentialDefinitionProjectionSchema,
        })
        .strict();

export const RpaBuilderLoadWebsiteCredentialDefinitionRequestSchema: z.ZodType<RpaBuilderLoadWebsiteCredentialDefinitionRequest> =
    z
        .object({
            website_scope: IpxRbsWorkflowWebsiteScopeSchema,
        })
        .strict();

export const RpaBuilderLoadWebsiteCredentialDefinitionResponseSchema: z.ZodType<RpaBuilderLoadWebsiteCredentialDefinitionResponse> =
    z
        .object({
            credential_definition: RpaBuilderCredentialDefinitionProjectionSchema.nullable(),
        })
        .strict();

export const RbsWebsiteStorageVersionSchema: z.ZodType<RbsWebsiteStorageVersion> = z.literal(
    RbsWebsiteStorageVersions.V1
);

export const RbsWorkflowStorageV1Schema: z.ZodType<RbsWorkflowStorageV1> = z
    .object({
        workflow: StoredIpxDomRpaWorkflowSchema,
        input_bindings: StoredIpxRbsWorkflowElementBindingListSchema,
        output_bindings: StoredIpxRbsWorkflowElementBindingListSchema,
        class_metadata: z.record(z.string(), JsonValueSchema),
    })
    .strict();

export const RbsWorkflowMapStorageV1Schema: z.ZodType<RbsWorkflowMapStorageV1> = z
    .object({
        [IpxRbsWorkflowClass.ENABLE_PAYMENTS]: z.array(RbsWorkflowStorageV1Schema),
        [IpxRbsWorkflowClass.TRANSACTION_DETAILS_DETECTION]: z.array(RbsWorkflowStorageV1Schema),
        [IpxRbsWorkflowClass.ENABLE_RECONCILIATION]: z.array(RbsWorkflowStorageV1Schema),
        [IpxRbsWorkflowClass.RECONCILE]: z.array(RbsWorkflowStorageV1Schema),
    })
    .strict();

export const RbsWebsiteStorageV1Schema: z.ZodType<RbsWebsiteStorageV1> = z
    .object({
        rbs_website_version: z.literal(RbsWebsiteStorageVersions.V1),
        rbs_config: StoredRpaBuilderWebsiteConfigSchema,
        web_resource_model: StoredIpxWebResourceModelDocumentSchema.nullable(),
        login_workflow: StoredIpxDomRpaWorkflowSchema.nullable(),
        workflows: RbsWorkflowMapStorageV1Schema,
    })
    .strict();

export const StoredRbsWebsiteStorageV1Schema: z.ZodType<RbsWebsiteStorageV1> = z.preprocess(
    (value: unknown): unknown =>
        normalizeStoredJsonSafeIntegerObjectFields(value, {
            domain: 'rbs-website',
            fields: ['rbs_website_version'],
            minimum: 0,
            maximum: Number.MAX_SAFE_INTEGER,
        }),
    RbsWebsiteStorageV1Schema
);

export const StoredRbsWebsiteSchema: z.ZodType<RbsWebsiteStorageV1> =
    StoredRbsWebsiteStorageV1Schema;

export function normalizeRbsWorkflowStorageV1(workflow: RbsWorkflowStorageV1): RbsWorkflow {
    return RbsWorkflowSchema.parse({
        workflow: StoredIpxDomRpaWorkflowParser.parse(workflow.workflow),
        input_bindings: StoredIpxRbsWorkflowElementBindingListParser.parse(workflow.input_bindings),
        output_bindings: StoredIpxRbsWorkflowElementBindingListParser.parse(
            workflow.output_bindings
        ),
        class_metadata: workflow.class_metadata,
    });
}

function normalizeRbsWorkflowListStorageV1(
    workflows: readonly RbsWorkflowStorageV1[]
): readonly RbsWorkflow[] {
    return workflows.map(normalizeRbsWorkflowStorageV1);
}

export function normalizeRbsWebsiteStorageV1(website: RbsWebsiteStorageV1): RbsWebsite {
    return RbsWebsiteSchema.parse({
        rbs_config: StoredRpaBuilderWebsiteConfigParser.parse(website.rbs_config),
        web_resource_model:
            website.web_resource_model === null
                ? null
                : StoredIpxWebResourceModelDocumentParser.parse(website.web_resource_model),
        login_workflow:
            website.login_workflow === null
                ? null
                : StoredIpxDomRpaWorkflowParser.parse(website.login_workflow),
        workflows: {
            [IpxRbsWorkflowClass.ENABLE_PAYMENTS]: normalizeRbsWorkflowListStorageV1(
                website.workflows[IpxRbsWorkflowClass.ENABLE_PAYMENTS]
            ),
            [IpxRbsWorkflowClass.TRANSACTION_DETAILS_DETECTION]: normalizeRbsWorkflowListStorageV1(
                website.workflows[IpxRbsWorkflowClass.TRANSACTION_DETAILS_DETECTION]
            ),
            [IpxRbsWorkflowClass.ENABLE_RECONCILIATION]: normalizeRbsWorkflowListStorageV1(
                website.workflows[IpxRbsWorkflowClass.ENABLE_RECONCILIATION]
            ),
            [IpxRbsWorkflowClass.RECONCILE]: normalizeRbsWorkflowListStorageV1(
                website.workflows[IpxRbsWorkflowClass.RECONCILE]
            ),
        },
    });
}

export const RbsWebsiteStorageContract = defineStoredJsonContract({
    contractName: 'RbsWebsite',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'StoredRbsWebsiteStorageV1Schema',
            schema: StoredRbsWebsiteStorageV1Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['rbs_website_version'],
                    markerValue: RbsWebsiteStorageVersions.V1,
                },
            ],
            normalize: normalizeRbsWebsiteStorageV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v1',
});

export const StoredRbsWebsiteParser: z.ZodType<RbsWebsite> =
    buildStoredJsonValueParser(RbsWebsiteStorageContract);

function serializeRbsWorkflowV1(workflow: RbsWorkflow): RbsWorkflowStorageV1 {
    return {
        workflow: serializeIpxDomRpaWorkflowV2(workflow.workflow),
        input_bindings: serializeIpxRbsWorkflowElementBindingListV2(workflow.input_bindings),
        output_bindings: serializeIpxRbsWorkflowElementBindingListV2(workflow.output_bindings),
        class_metadata: workflow.class_metadata,
    };
}

function serializeRbsWorkflowListV1(
    workflows: readonly RbsWorkflow[]
): readonly RbsWorkflowStorageV1[] {
    return workflows.map(serializeRbsWorkflowV1);
}

export function serializeRbsWebsiteV1(website: RbsWebsite): RbsWebsiteStorageV1 {
    const currentWebsite: RbsWebsite = RbsWebsiteSchema.parse(website);
    return {
        rbs_website_version: RbsWebsiteStorageVersions.V1,
        rbs_config: serializeRpaBuilderWebsiteConfigV2(currentWebsite.rbs_config),
        web_resource_model:
            currentWebsite.web_resource_model === null
                ? null
                : serializeIpxWebResourceModelDocumentV2(currentWebsite.web_resource_model),
        login_workflow:
            currentWebsite.login_workflow === null
                ? null
                : serializeIpxDomRpaWorkflowV2(currentWebsite.login_workflow),
        workflows: {
            [IpxRbsWorkflowClass.ENABLE_PAYMENTS]: serializeRbsWorkflowListV1(
                currentWebsite.workflows[IpxRbsWorkflowClass.ENABLE_PAYMENTS]
            ),
            [IpxRbsWorkflowClass.TRANSACTION_DETAILS_DETECTION]: serializeRbsWorkflowListV1(
                currentWebsite.workflows[IpxRbsWorkflowClass.TRANSACTION_DETAILS_DETECTION]
            ),
            [IpxRbsWorkflowClass.ENABLE_RECONCILIATION]: serializeRbsWorkflowListV1(
                currentWebsite.workflows[IpxRbsWorkflowClass.ENABLE_RECONCILIATION]
            ),
            [IpxRbsWorkflowClass.RECONCILE]: serializeRbsWorkflowListV1(
                currentWebsite.workflows[IpxRbsWorkflowClass.RECONCILE]
            ),
        },
    };
}
